'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Search, Users, Loader2, Lock, ChevronRight, ChevronDown, AlertTriangle, ArrowUpRight } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { getCongressMembers, getCongressMember, type CongressMember } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import { useTimezone } from '@/hooks/useTimezone';
import ClonePortfolioPopup, { type CloneHolding } from './ClonePortfolioPopup';

/** Round portrait with a party-colored border. Falls back to initials if the
 *  bioguide photo 404s (not every member has one uploaded). */
function MemberPhoto({ photoUrl, name, party, size }: { photoUrl?: string; name: string; party: string; size: number }) {
  const [failed, setFailed] = useState(false);
  const initials = name.split(/\s+/).filter(Boolean).map((n) => n[0]).join('').slice(0, 2).toUpperCase();
  const borderColor = party === 'D' ? '#3b82f6' : party === 'R' ? '#ef4444' : '#64748b';
  const fallbackBg = party === 'D' ? 'bg-blue-500/30 text-blue-200' : party === 'R' ? 'bg-red-500/30 text-red-200' : 'bg-slate-600/40 text-slate-200';
  if (!photoUrl || failed) {
    return (
      <div className={cn('rounded-full flex items-center justify-center font-bold flex-shrink-0 border-2', fallbackBg)}
           style={{ width: size, height: size, fontSize: size * 0.35, borderColor }}>
        {initials || '?'}
      </div>
    );
  }
  return (
    // eslint-disable-next-line @next/next/no-img-element
    <img src={photoUrl} alt={name} className="rounded-full object-cover flex-shrink-0 border-2"
         style={{ width: size, height: size, borderColor }}
         onError={() => setFailed(true)} />
  );
}

/** Browse + clone UI for congressional members. Rendered as the Directory tab
 *  on /congress. Safe to render as a standalone page too. */
export default function CongressionalDirectory({ returnTo = '/congress?tab=directory' }: { returnTo?: string }) {
  const { user, isPro } = useAuth();
  const tz = useTimezone();
  const router = useRouter();
  const [members, setMembers] = useState<CongressMember[]>([]);
  const [pageLoading, setPageLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [partyFilter, setPartyFilter] = useState('');
  const [chamberFilter, setChamberFilter] = useState('');
  const [selectedMember, setSelectedMember] = useState<CongressMember | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const detailRef = useRef<HTMLDivElement>(null);

  // When a member is picked, scroll the detail panel into view. The list can
  // grow long enough that a selection from the bottom of the grid leaves the
  // panel off-screen even with its `sticky top-20` — this is the reliable fix.
  useEffect(() => {
    if (!selectedMember) return;
    const el = detailRef.current;
    if (!el) return;
    // Defer one frame so the panel has finished rendering the new member.
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, [selectedMember?.id]);
  const [cloneError, setCloneError] = useState('');
  const [cloneDialogOpen, setCloneDialogOpen] = useState(false);
  const [cloneHoldings, setCloneHoldings] = useState<CloneHolding[] | null>(null);
  const [cloneLoading, setCloneLoading] = useState(false);

  useEffect(() => {
    getCongressMembers({ search, party: partyFilter, chamber: chamberFilter })
      .then(setMembers)
      .catch(() => {})
      .finally(() => setPageLoading(false));
  }, [search, partyFilter, chamberFilter]);

  const selectMember = async (id: string) => {
    // Clicking the currently-expanded row collapses it.
    if (selectedMember?.id === id) { setSelectedMember(null); return; }
    setDetailLoading(true);
    setCloneError('');
    try {
      const m = await getCongressMember(id);
      setSelectedMember(m);
    } catch { /* ignore */ }
    finally { setDetailLoading(false); }
  };

  const handleCloneClick = async () => {
    if (!selectedMember) return;
    if (!user) { router.push(`/login?returnTo=${encodeURIComponent(returnTo)}`); return; }
    setCloneError('');
    setCloneLoading(true);
    // Pull the richer holdings list (with estimated values for the mirror
    // strategy) from the member history endpoint. Fall back to the bare
    // disclosed tickers if that fetch fails.
    try {
      const qs = new URLSearchParams({ name: selectedMember.name, party: selectedMember.party, chamber: selectedMember.chamber }).toString();
      const r = await fetch(`/api/congress/member/${selectedMember.id}/history?${qs}`);
      if (r.ok) {
        const j = await r.json() as { portfolio?: Array<{ ticker: string; estimatedMax?: number }> };
        const holdings = (j.portfolio ?? []).map((h) => ({ ticker: h.ticker, estimatedValue: h.estimatedMax }));
        setCloneHoldings(holdings.length > 0 ? holdings : (selectedMember.disclosed_tickers ?? []).map((t) => ({ ticker: t })));
      } else {
        setCloneHoldings((selectedMember.disclosed_tickers ?? []).map((t) => ({ ticker: t })));
      }
    } catch {
      setCloneHoldings((selectedMember.disclosed_tickers ?? []).map((t) => ({ ticker: t })));
    } finally {
      setCloneLoading(false);
      setCloneDialogOpen(true);
    }
  };

  const partyColor = (p: string) => p === 'D' ? 'text-blue-400 bg-blue-500/15' : p === 'R' ? 'text-red-400 bg-red-500/15' : 'text-fg-muted bg-slate-500/15';

  return (
    <div>
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="relative flex-1 min-w-[200px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-fg-subtle" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name or state..."
            className="w-full bg-card-bg border border-default rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-slate-500 focus:outline-none focus:border-sky-500/50"
          />
        </div>
        <select value={partyFilter} onChange={(e) => setPartyFilter(e.target.value)}
          className="bg-card-bg border border-default rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500/50">
          <option value="">All Parties</option>
          <option value="D">Democrat</option>
          <option value="R">Republican</option>
        </select>
        <select value={chamberFilter} onChange={(e) => setChamberFilter(e.target.value)}
          className="bg-card-bg border border-default rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-sky-500/50">
          <option value="">All Chambers</option>
          <option value="House">House</option>
          <option value="Senate">Senate</option>
        </select>
      </div>

      {pageLoading ? (
        <div className="flex justify-center py-12"><Loader2 size={24} className="animate-spin text-fg-subtle" /></div>
      ) : members.length === 0 ? (
        <div className="bg-card-bg border border-default rounded-xl p-8 text-center">
          <Users size={32} className="text-slate-600 mx-auto mb-3" />
          <p className="text-sm text-fg-subtle">No members found. Try adjusting your filters.</p>
        </div>
      ) : (
        <div className="bg-card-bg border border-default rounded-xl overflow-hidden divide-y divide-border-default/50">
          {members.map((m) => {
            const isOpen = selectedMember?.id === m.id;
            return (
              <div key={m.id} ref={isOpen ? detailRef : undefined} className="scroll-mt-24">
                <button
                  onClick={() => selectMember(m.id)}
                  className={cn(
                    'w-full text-left px-4 py-3 hover:bg-card-hover-bg transition-colors flex items-center gap-3',
                    isOpen && 'bg-card-hover-bg',
                  )}
                  aria-expanded={isOpen}
                >
                  <MemberPhoto photoUrl={m.photo_url} name={m.name} party={m.party} size={36} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-sm font-medium text-white">{m.name}</span>
                      <span className={cn('text-[10px] font-bold px-1.5 py-0.5 rounded', partyColor(m.party))}>
                        {m.party}
                      </span>
                    </div>
                    <div className="text-xs text-fg-subtle">
                      {[
                        m.state,
                        m.chamber,
                        `${m.ticker_count ?? 0} tickers`,
                        m.last_disclosure_date ? `Last filed ${new Date(m.last_disclosure_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: tz })}` : null,
                      ].filter(Boolean).join(' | ')}
                    </div>
                  </div>
                  {isOpen ? <ChevronDown size={14} className="text-fg-muted flex-shrink-0" /> : <ChevronRight size={14} className="text-slate-600 flex-shrink-0" />}
                </button>

                {isOpen && (
                  <div className="px-4 py-4 bg-surface/60 border-t border-default/50">
                    {detailLoading || !selectedMember ? (
                      <div className="flex items-center justify-center py-6">
                        <Loader2 size={18} className="animate-spin text-fg-subtle" />
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div>
                          <h4 className="text-[11px] font-medium text-fg-muted uppercase tracking-wide mb-1.5">Disclosed Tickers</h4>
                          <div className="flex flex-wrap gap-1.5">
                            {(selectedMember.disclosed_tickers ?? []).map((t) => (
                              <Link
                                key={t}
                                href={`/stocks/${t}`}
                                className="text-xs font-mono text-sky-400 bg-sky-500/10 hover:bg-sky-500/20 hover:text-sky-300 px-2 py-1 rounded transition-colors"
                              >
                                {t}
                              </Link>
                            ))}
                            {(selectedMember.disclosed_tickers ?? []).length === 0 && (
                              <p className="text-xs text-fg-subtle">No tickers disclosed</p>
                            )}
                          </div>
                        </div>

                        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2">
                          <p className="text-[11px] text-amber-300 flex items-start gap-1.5">
                            <AlertTriangle size={12} className="flex-shrink-0 mt-0.5" />
                            Based on public STOCK Act disclosures. This is not investment advice.
                          </p>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-2">
                          {isPro ? (
                            <button
                              onClick={handleCloneClick}
                              disabled={cloneLoading || (selectedMember.disclosed_tickers ?? []).length === 0}
                              className="flex-1 bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-sm font-medium py-2 rounded-lg transition-colors"
                            >
                              {cloneLoading ? 'Loading…' : 'Clone to Portfolio'}
                            </button>
                          ) : (
                            <button
                              onClick={() => router.push('/pro')}
                              className="flex-1 bg-card-hover-bg border border-default text-fg-muted text-sm font-medium py-2 rounded-lg flex items-center justify-center gap-2"
                            >
                              <Lock size={14} /> Upgrade to Pro to Clone
                            </button>
                          )}
                          <Link
                            href={`/congress/member/${selectedMember.id}?name=${encodeURIComponent(selectedMember.name)}&party=${encodeURIComponent(selectedMember.party || '')}&chamber=${encodeURIComponent(selectedMember.chamber || '')}`}
                            className="flex-1 bg-card-hover-bg border border-default hover:border-sky-500/40 text-sky-400 hover:text-sky-300 text-sm font-medium py-2 rounded-lg flex items-center justify-center gap-1.5 transition-colors"
                          >
                            View profile &amp; trades <ArrowUpRight size={12} />
                          </Link>
                        </div>

                        {cloneError && <p className="text-xs text-red-400">{cloneError}</p>}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {cloneDialogOpen && selectedMember && cloneHoldings && (
        <ClonePortfolioPopup
          memberId={selectedMember.id}
          memberName={selectedMember.name}
          holdings={cloneHoldings}
          isPro={isPro}
          onClose={() => setCloneDialogOpen(false)}
          onCreated={(id) => router.push(`/portfolio/${id}`)}
        />
      )}
      {cloneError && <p className="text-xs text-red-400 mt-2">{cloneError}</p>}
    </div>
  );
}
